import { useState } from 'react';
import { Pressable, Text, View, ActivityIndicator, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { trpc } from '../lib/trpc';
import { Card } from './Card';
import { ResponsiveModal } from './ResponsiveModal';
import { Button } from './Button';

type PlanHealthIssue = {
  id: string;
  checkId: string;
  severity: 'info' | 'warning';
  title: string;
  description: string;
  ctaLabel: string;
  ctaHref: string;
};

type PlanHealthNavigationContext = 'app' | 'onboarding';

const ONBOARDING_CTA_HREF_BY_CHECK_ID: Record<string, string> = {
  no_income_streams: '/(onboarding)/step-4-income',
  wages_missing_deductions: '/(onboarding)/step-4-income',
  unassigned_wage_person: '/(onboarding)/step-4-income',
  missing_social_security: '/(onboarding)/step-4-income',
  pension_no_end_date: '/(onboarding)/step-4-income',
  empty_budget: '/(onboarding)/step-5-budget',
  missing_life_expectancy: '/(onboarding)/step-7-life-events',
  missing_state: '/(onboarding)/step-1-about-you',
  missing_filing_status: '/(onboarding)/step-1-about-you',
  accounts_need_tax_review: '/(onboarding)/step-2-connect',
  taxable_holdings_no_cost_basis: '/(onboarding)/step-2-connect',
  stale_account_balances: '/(onboarding)/step-2-connect',
  no_life_events: '/(onboarding)/step-7-life-events',
  dependent_without_education_prefs: '/(onboarding)/step-6-family',
  no_tax_strategy_configured: '/(onboarding)/step-7-life-events',
  risk_tolerance_unset: '/(onboarding)/step-7-life-events',
  asset_no_growth_rate: '/(onboarding)/step-2-connect',
  mortgage_missing_breakdown: '/(onboarding)/step-2-connect',
};

export function getPlanHealthIssueHref(
  issue: PlanHealthIssue,
  navigationContext: PlanHealthNavigationContext,
) {
  if (navigationContext === 'onboarding') {
    return ONBOARDING_CTA_HREF_BY_CHECK_ID[issue.checkId] ?? issue.ctaHref;
  }
  return issue.ctaHref;
}

type PlanHealthIssueListProps = {
  issues: PlanHealthIssue[];
  isLoading?: boolean;
  isFetching?: boolean;
  navigationContext?: PlanHealthNavigationContext;
  onIssuePress?: (issue: PlanHealthIssue, href: string) => void | Promise<void>;
  onRefetch?: () => void;
  scrollable?: boolean;
  className?: string;
};

export function PlanHealthIssueList({
  issues,
  isLoading = false,
  isFetching = false,
  navigationContext = 'app',
  onIssuePress,
  onRefetch,
  scrollable = true,
  className = '',
}: PlanHealthIssueListProps) {
  const router = useRouter();

  const handleIssuePress = async (issue: PlanHealthIssue) => {
    const href = getPlanHealthIssueHref(issue, navigationContext);
    if (onIssuePress) {
      await onIssuePress(issue, href);
      return;
    }
    router.push(href as never);
  };

  const content = (
    <>
      {isLoading && (
        <View className="py-6 items-center">
          <ActivityIndicator />
        </View>
      )}
      <View className="gap-3 mt-2">
        {issues.map((issue) => (
          <View
            key={issue.id}
            className="rounded-xl border border-gray-200 dark:border-gray-700 p-4"
          >
            <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {issue.title}
            </Text>
            <Text className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {issue.description}
            </Text>
            <View className="mt-3 flex-row">
              <Button
                size="sm"
                onPress={() => {
                  void handleIssuePress(issue);
                }}
              >
                {issue.ctaLabel}
              </Button>
            </View>
          </View>
        ))}
      </View>
      {onRefetch && (
        <Pressable
          onPress={onRefetch}
          className="mt-4 self-center py-2"
        >
          <Text className="text-sm text-green-700 dark:text-green-400 font-semibold">
            {isFetching ? 'Re-scanning…' : 'Re-scan'}
          </Text>
        </Pressable>
      )}
    </>
  );

  if (!scrollable) {
    return <View className={className}>{content}</View>;
  }

  return (
    <ScrollView className={className || 'px-5 pb-5'}>
      {content}
    </ScrollView>
  );
}

type PlanHealthBannerProps = {
  navigationContext?: PlanHealthNavigationContext;
  onIssuePress?: (issue: PlanHealthIssue, href: string) => void | Promise<void>;
};

export function PlanHealthBanner({
  navigationContext = 'app',
  onIssuePress,
}: PlanHealthBannerProps = {}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const scanQ = trpc.planHealth.scan.useQuery(undefined, {
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const issues = scanQ.data?.issues ?? [];
  if (issues.length === 0) return null;

  const count = issues.length;

  return (
    <>
      <Card className="border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30">
        <Pressable
          onPress={() => setOpen(true)}
          className="flex-row items-center gap-3"
        >
          <View className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-800 items-center justify-center">
            <Ionicons name="alert-circle" size={20} color="#b45309" />
          </View>
          <View className="flex-1">
            <Text className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {count} suggested {count === 1 ? 'action' : 'actions'} for your plan
            </Text>
            <Text className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
              Tap to review items that may improve your FIRE projection.
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color="#b45309" />
        </Pressable>
      </Card>

      <ResponsiveModal
        visible={open}
        onClose={() => setOpen(false)}
        header={
          <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Plan health
          </Text>
        }
        subtitle={
          <Text className="text-xs text-gray-500 dark:text-gray-400">
            Suggested actions that may improve the accuracy of your FIRE plan.
          </Text>
        }
      >
        <PlanHealthIssueList
          issues={issues}
          isLoading={scanQ.isLoading}
          isFetching={scanQ.isFetching}
          navigationContext={navigationContext}
          onIssuePress={async (issue, href) => {
            setOpen(false);
            if (onIssuePress) {
              await onIssuePress(issue, href);
              return;
            }
            router.push(href as never);
          }}
          onRefetch={() => {
            void scanQ.refetch();
          }}
        />
      </ResponsiveModal>
    </>
  );
}
